import { ref } from "vue";
import * as XLSX from "xlsx";

// Define interfaces for type safety
interface ContactData {
  email: string | null;
  name: string | null;
  number: string | null;
  company: string | null;
  is_private: boolean;
}

interface SheetRow {
  [key: string]: unknown;
  __rowNum__?: number;
}

const isValidPhoneNumber = (input: string): boolean => {
  return /^\d+$/.test(input);
};

const formatPhoneNumber = (phoneNumber: string | number): string => {
  // Convert phoneNumber to a string safely
  const phoneStr = String(phoneNumber);

  // Remove the leading + if present to standardize the format
  const standardizedPhoneNumber = phoneStr.startsWith("+")
    ? phoneStr.substring(1)
    : phoneStr;

  // Check if standardizedPhoneNumber starts with 234
  if (standardizedPhoneNumber.startsWith("234")) {
    // Check if there's a 0 immediately after 234, if so, drop the 234, otherwise replace 234 with 0
    return standardizedPhoneNumber[3] === "0"
      ? standardizedPhoneNumber.substring(3)
      : "0" + standardizedPhoneNumber.substring(3);
  }
  if (!standardizedPhoneNumber.startsWith("0")) {
    return "0" + standardizedPhoneNumber;
  }

  // Return the original phoneNumber if it doesn't start with +234 or 234
  return phoneStr;
};

const isValidEmail = (input: string): boolean => {
  if (input == "") {
    return true;
  }
  return /.+@.+\..+/.test(input);
};

const allDataArray = ref<ContactData[]>([]);

const isPrivate = ref(true);

const singleMatchAction = ref("NO_ACTION");

const multipleMatchResolutionStrategy = ref("USE_RECENTLY_CREATED");

const headers = ref<string[]>();

export const useContact = () => {
  // central state to keep array of all imported contacts

  const fileUpload = async (file: File) => {
    const fileReader = new FileReader();

    fileReader.readAsArrayBuffer(file);

    const sheetDataArray = [] as string[][];

    let lowerCaseHeaders;

    await new Promise((resolve, reject) => {
      fileReader.onload = resolve;
      fileReader.onerror = reject;
    });

    const arrayBuffer = fileReader.result as ArrayBuffer;

    const workbook = XLSX.read(arrayBuffer, { type: "array" });

    const errorIndexes = [] as number[];

    workbook.SheetNames.forEach((sheetName) => {
      // const XL_row_obj = XLSX.utils.sheet_to_row_obje
      const sheet = workbook.Sheets[sheetName];

      const data = XLSX.utils.sheet_to_json(sheet, {
        blankrows: false,
        defval: "",
      });

      const headers = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
      })[0] as string[];

      //NB: Convert all headers to lowercase for case-insensitive headers
      lowerCaseHeaders = headers.map((header) => header.toLowerCase());

      //NB: Define the headers that are required. At least this two must be there
      // const requiredHeaders = ["name", "phone number"];

      //NB: Define the headers that are allowed (including the required ones). This is to check and prevent upload for users that may upload wrong headers and comeback to say they cant find their information
      const allowedHeaders = ["name", "phone number", "email", "company"];

      lowerCaseHeaders.forEach((element, index: number) => {
        const containsElement = allowedHeaders.includes(element.toLowerCase());
        if (!containsElement) {
          errorIndexes.push(index);
        }
      });

      // NB:  Disable upload for empty file
      if (data.length === 0) {
        throw new Error(
          "The uploaded file is empty. Please ensure the file contains data and try again." // check for empty upload
        );
      }

      //NB: Find the actual header name eg for "NAME" header any case is allowed so far it is the correct combination.
      const nameHeader = headers[lowerCaseHeaders.indexOf("name")];
      const phoneNumberHeader =
        headers[lowerCaseHeaders.indexOf("phone number")];

      // const emailHeader = headers[lowerCaseHeaders.indexOf("email")];
      //NB: Find the actual header name eg for "NAME" header any case is allowed so far it is the correct combination.
      data.forEach((rowData) => {
        const row = rowData as SheetRow;
        sheetDataArray.push(Object.values(row).map(String));

        if (nameHeader && !row[nameHeader]) {
          throw new Error(
            `The name at row ${
              ((row["__rowNum__"] as number) || 0) + 1
            }  is empty. Please ensure you fill the field and try again.`
          );
        }

        if (phoneNumberHeader && !row[phoneNumberHeader]) {
          throw new Error(
            `The phone number at row ${
              ((row["__rowNum__"] as number) || 0) + 1
            }  is empty. Please ensure you fill the field and try again.`
          );
        }

        if (
          phoneNumberHeader &&
          !isValidPhoneNumber(String(row[phoneNumberHeader]))
        ) {
          throw new Error(
            `The phone number at row ${((row["__rowNum__"] as number) || 0) + 1} "${
              row["PHONE NUMBER"] ? String(row["PHONE NUMBER"]) : ""
            }" is not valid. Phone number must contain only digit`
          );
        }

        if (phoneNumberHeader && String(row[phoneNumberHeader])?.length < 7) {
          throw new Error(
            `The phone number at row ${((row["__rowNum__"] as number) || 0) + 1} *${
              row[phoneNumberHeader] ? String(row[phoneNumberHeader]) : ""
            }* is not valid. Please ensure it contains a valid phone number and try again`
          );
        }
      });
    });

    return { errorIndexes, lowerCaseHeaders, sheetDataArray };
  };

  const validateContacts = (
    headers: string[],
    sheetDataArray: string[][],
    isPrivate: boolean
  ) => {
    allDataArray.value = [];
    const nameHeader = safeIndexOf(headers, "name");
    const phoneNumberHeader = safeIndexOf(headers, "phone number");
    const companyHeader = safeIndexOf(headers, "company");
    const emailHeader = safeIndexOf(headers, "email");

    const requiredHeaders = ["name", "phone number"];

    const lowerCaseHeaders = headers.map((header) => header.toLowerCase());

    //  NB: Check if all required headers are present. So we allow people upload at least those two.
    if (!requiredHeaders.every((header) => lowerCaseHeaders.includes(header))) {
      // NB: throw error if the required header arent present
      throw new Error(
        "Invalid headers. Headers must include 'NAME' and 'PHONE NUMBER'."
      );
    }

    sheetDataArray.forEach((row: string[], index) => {
      if (nameHeader && !row[nameHeader]) {
        throw new Error(
          `The name at row ${
            index + 1
          }  is empty. Please ensure you fill the field and try again.`
        );
      }

      if (phoneNumberHeader && !row[phoneNumberHeader]) {
        throw new Error(
          `The phone number at row ${
            index + 1
          }  is empty. Please ensure you fill the field and try again.`
        );
      }

      if (emailHeader && !isValidEmail(row[emailHeader])) {
        throw new Error(
          `The email at row ${index + 1} "${
            row[emailHeader]
          }" is not valid. Email must be a valid email address`
        );
      }

      if (phoneNumberHeader && !isValidPhoneNumber(row[phoneNumberHeader])) {
        throw new Error(
          `The phone number at row ${index + 1} "${
            row[phoneNumberHeader]
          }" is not valid. Phone number must contain only digit`
        );
      }

      if (phoneNumberHeader && row[phoneNumberHeader]?.toString()?.length < 7) {
        throw new Error(
          `The phone number at row ${index + 1} *${
            row[phoneNumberHeader]
          }* is not valid. Please ensure it contains a valid phone number and try again`
        );
      }

      const obj: ContactData = {
        email:
          emailHeader && row[emailHeader] ? String(row[emailHeader]) : null,
        // this is because an index of zero resolves to false
        name: row[nameHeader!] ? String(row[nameHeader!]) : null,
        number:
          phoneNumberHeader && row[phoneNumberHeader]
            ? formatPhoneNumber(row[phoneNumberHeader] as string | number)
            : null,
        company:
          companyHeader && row[companyHeader]
            ? String(row[companyHeader])
            : null,
        is_private: isPrivate,
      };

      allDataArray.value.push(obj);
    });
  };

  return {
    validateContacts,
    allDataArray,
    isPrivate,
    fileUpload,
    singleMatchAction,
    multipleMatchResolutionStrategy,
    headers,
  };
};

function safeIndexOf<T>(arr: T[], item: T): number | null {
  const index = arr.indexOf(item);
  return index !== -1 ? index : null;
}
